import type { ColumnDef } from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import { CellAction } from './cell-action';
import type { Inquiry } from '@/services/types';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, Mail, Calendar } from 'lucide-react';

export const createColumns = (): ColumnDef<Inquiry>[] => [
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => {
      const email = row.getValue<string>('email');
      return (
        <div className='flex items-center space-x-2'>
          <Mail className='h-4 w-4 text-gray-500' />
          <span className='truncate max-w-[200px]'>{email}</span>
        </div>
      );
    }
  },
  {
    accessorKey: 'subject',
    header: 'Subject',
    cell: ({ row }) => {
      const subject = row.getValue<string>('subject');
      return (
        <div className='max-w-[300px]'>
          <p className='truncate font-medium'>
            {subject || 'No Subject'}
          </p>
        </div>
      );
    }
  },
  {
    accessorKey: 'message',
    header: 'Message',
    cell: ({ row }) => {
      const message = row.getValue<string>('message');
      return (
        <div className='max-w-[400px]'>
          <p className='truncate text-gray-600'>
            {message}
          </p>
        </div>
      );
    }
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue<string>('status');
      const isResolved = status === 'resolved';
      
      return (
        <Badge
          variant='outline'
          className={cn(
            'flex items-center gap-1 border-0 text-xs font-medium',
            isResolved
              ? 'bg-green-50 text-green-700 hover:bg-green-100'
              : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
          )}
        >
          {isResolved ? (
            <CheckCircle className='h-3 w-3' />
          ) : (
            <Clock className='h-3 w-3' />
          )}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      );
    }
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => {
      const date = row.getValue<Date>('createdAt');
      return (
        <div className='flex items-center space-x-2'>
          <Calendar className='h-4 w-4 text-gray-500' />
          <span className='text-sm'>
            {new Date(date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}
          </span>
        </div>
      );
    }
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => <CellAction data={row.original} />
  }
];